import React, { useState } from 'react';
import { X, Play, Plus, ThumbsUp, ThumbsDown, Share } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import VideoPlayer from './VideoPlayer';
import FullscreenVideoPlayer from './FullscreenVideoPlayer';

const MovieModal = ({ movie, isOpen, onClose, allVideos, setSelectedMovie }) => {
  const [showVideo, setShowVideo] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);

  const handleCloseModal = () => {
    console.log('handleCloseModal');
    if (window.history.replaceState) {
      window.history.replaceState(null, '', '/');
    } else {
      console.warn('History API not supported');
    }
    onClose();
  };

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleCloseModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleCloseModal]);

  if (!isOpen || !movie) return null;

  // Helper function to get the correct image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    // If it's already a full URL (starts with http), use it directly
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    // Otherwise, use TMDB prefix
    return `images/${imagePath}`;
  };

  // Helper function to sanitize and parse HTML description
  const parseDescription = (description) => {
    if (!description) return '';
    
    // Basic HTML sanitization - allow only safe tags
    const allowedTags = ['a', 'br', 'p', 'strong', 'em', 'b', 'i', 'u'];
    const allowedAttributes = {
      'a': ['href', 'target', 'rel']
    };
    
    // Simple sanitization - remove script tags and dangerous attributes
    let sanitized = description
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
      .replace(/on\w+="[^"]*"/gi, '')
      .replace(/javascript:/gi, '');
    
    // Add target="_blank" and rel="noopener noreferrer" to links for security
    sanitized = sanitized.replace(/<a\s+href="([^"]*)"[^>]*>/gi, (match, href) => {
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-blue-400 hover:text-blue-300 underline">`;
    });
    
    return sanitized;
  };

  // Helper function to get genre names
  const getGenreName = (genreId) => {
    const genres = {
      30: 'Mixed Martial Arts', 31: 'Kickboxing', 32: 'Boxing', 33: 'Brazilian Jiu-Jitsu', 34: 'Judo',
      100: 'Metal', 101: 'Hard Rock', 102: 'Death Metal', 103: 'Post-Rock', 104: 'Black Metal',
      200: 'Reggaeton', 201: 'Salsa', 202: 'Bachata', 203: 'Cumbia', 204: 'Hip-Hop',
      300: 'Standup', 301: 'Chronique',
      400: 'Trail', 401: 'Running', 402: 'Reportage'
    };
    return genres[genreId] || 'Unknown';
  };

  const handlePlayClick = (movie) => {
    const { titleSeo } = movie;
    if (window.history.pushState) {
      window.history.pushState(null, '', `/${titleSeo}`);
    } else {
      console.warn('History API not supported');
    }

    // Lancer directement le lecteur plein écran sans fermer la modal
    setShowFullscreen(true);
  };

  const handleCloseVideo = () => {
    setShowVideo(false);
  };

  const handleFullscreenPlay = () => {
    setShowFullscreen(true);
  };

  const handleCloseFullscreen = () => {
    // Réinitialiser l'URL à sa valeur d'origine
    if (window.history.replaceState) {
      window.history.replaceState(null, '', '/');
    } else {
      console.warn('History API not supported');
    }

    setShowFullscreen(false);
  };

  const handleCloseFullscreenAndModal = () => {
    // Réinitialiser l'URL à sa valeur d'origine
    if (window.history.replaceState) {
      window.history.replaceState(null, '', '/');
    } else {
      console.warn('History API not supported');
    }

    setShowFullscreen(false);
    setShowVideo(false);
    onClose(); // Fermer la modal après avoir fermé le lecteur plein écran
  };

  const handleSetCurrentVideo = (video) => {
    setSelectedMovie(video);
  };

  // Handle clicking outside the modal to close it
  const handleBackdropClick = (e) => {
    // Don't close if fullscreen video is open
    if (showFullscreen) return;
    
    // Only close if clicking on the backdrop (not on the modal content)
    if (e.target === e.currentTarget) {
      console.log('Backdrop clicked, closing modal'); // Debug
      onClose();
    }
  };

  if (!isOpen || !movie) return null;

  // Filter videos by the movie's label
  const videos = movie && movie.label ? allVideos.filter(video => video.label === movie.label) : [];

  return (
    <>
      {/* Modal - cachée si fullscreen est actif */}
      <div 
        className={`fixed inset-0 z-[10001] bg-black/80 backdrop-blur-sm ${showFullscreen ? 'hidden' : ''}`}
        onClick={handleBackdropClick}
      >
        <div className="flex items-center justify-center min-h-screen p-4" onClick={handleBackdropClick}>
          <div className="relative bg-gray-900 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
               onClick={(e) => e.stopPropagation()}
          >
          
            {/* Close Button */}
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70"
              onClick={handleCloseModal} // Updated to use handleCloseModal
            >
              <X className="w-6 h-6" />
            </Button>

            <div className="overflow-y-auto max-h-[90vh]">
              {/* Hero Image / Video Section */}
              <div className="relative h-96">
                {showVideo ? (
                  <VideoPlayer 
                    youtubeUrl={movie.trailerUrl || "https://www.youtube.com/watch?v=dQw4w9WgXcQ"} 
                    autoplay={true}
                    onClose={handleCloseVideo}
                    onFullscreen={handleFullscreenPlay}
                  />
                ) : (
                  <>
                    <img
                      src={getImageUrl(movie.backdrop_path || movie.poster_path)}
                      alt={movie.title || movie.name}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />
                    
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Button
                        size="lg"
                        className="bg-white hover:bg-white/80 text-black font-semibold px-8 py-4 text-lg"
                        onClick={() => handlePlayClick(movie)}
                      >
                        <Play className="w-6 h-6 fill-current mr-2" />
                        Play
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {/* Content Section */}
              <div className="p-8">
                {/* Title and Basic Info */}
                <div className="mb-6">
                  <h1 _ric={movie.id} className="text-4xl font-bold text-white mb-4">
                    {movie.title || movie.name}
                  </h1>
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-300 mb-4">
                    {/* Masqué: Match percentage */}
                    {false && (
                      <span className="text-green-400 font-semibold">
                        {Math.round(movie.vote_average * 10)}% Match
                      </span>
                    )}
                    
                    {/* Afficher le label/channel de la vidéo */}
                    {(movie.channelTitle || movie.label || movie.artist) && (
                      <span className="bg-red-600 px-3 py-1 rounded text-white font-medium">
                        {movie.channelTitle || movie.label || movie.artist}
                      </span>
                    )}
                    
                    {/* Afficher la vraie date de publication */}
                    {(movie.release_date || movie.first_air_date) && (
                      <span>
                        {new Date(movie.release_date || movie.first_air_date).toLocaleDateString()}
                      </span>
                    )}
                    
                    {/* Masqué: HD badge et durée */}
                    {false && (
                      <>
                        <Badge variant="outline" className="text-gray-300 border-gray-500">
                          HD
                        </Badge>
                        {movie.runtime && (
                          <span>{movie.runtime}m</span>
                        )}
                      </>
                    )}
                  </div>

                  {/* Genres */}
                  <div className="flex flex-wrap gap-2 mb-2">
                    {movie.genre_ids?.slice(0, 4).map((genreId) => (
                      <Badge key={genreId} variant="secondary" className="bg-gray-700 text-gray-200">
                        {getGenreName(genreId)}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-4 mb-8">
                  <Button
                    size="lg"
                    className="bg-white hover:bg-white/80 text-black font-semibold px-8"
                    onClick={handleFullscreenPlay}
                  >
                    <Play className="w-5 h-5 fill-current mr-2" />
                    Play
                  </Button>
                  
                  {/* Masqué: My List, Thumbs Up, Thumbs Down buttons */}
                  {false && (
                    <>
                      <Button
                        variant="outline"
                        size="lg"
                        className="border-gray-500 text-white hover:bg-gray-800 px-8"
                      >
                        <Plus className="w-5 h-5 mr-2" />
                        My List
                      </Button>

                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-3 rounded-full border border-gray-500 text-white hover:border-white hover:bg-gray-800"
                        >
                          <ThumbsUp className="w-5 h-5" />
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-3 rounded-full border border-gray-500 text-white hover:border-white hover:bg-gray-800"
                        >
                          <ThumbsDown className="w-5 h-5" />
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-3 rounded-full border border-gray-500 text-white hover:border-white hover:bg-gray-800"
                        >
                          <Share className="w-5 h-5" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                {/* Description and Details */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <h3 className="text-xl font-semibold text-white mb-4">Overview</h3>
                    <div 
                      className="text-gray-300 leading-relaxed text-lg"
                      dangerouslySetInnerHTML={{ __html: parseDescription(movie.overview) }}
                    />
                  </div>

                  <div className="space-y-4">
                    {
                      false ? (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-400 mb-1">Rating</h4>
                          <div className="flex items-center space-x-2">
                            <span className="text-white text-lg font-semibold">
                              {movie.vote_average?.toFixed(1)} ★
                            </span>
                            <span className="text-gray-400 text-sm">
                              ({movie.vote_count || 'N/A'} votes)
                            </span>
                          </div>
                        </div>
                      ) : null
                    }

                    <div>
                      <h4 className="text-sm font-semibold text-gray-400 mb-1">Release Date</h4>
                      <p className="text-white">
                        {new Date(movie.release_date || movie.first_air_date).toLocaleDateString()}
                      </p>
                    </div>

                    {/* Share Buttons - now below Release Date and Genres */}
                    <div className="flex flex-col gap-2 mb-2">
                      {(() => {
                        const { hostname, port } = window.location;
                        let shareUrl = '';
                        if ((hostname === 'tropicaliente.ch' || (hostname === 'localhost' && port === '3002')) && movie.pathseo) {
                          shareUrl = `https://sm.tropicaliente.ch/latino/${movie.pathseo}`;
                        } else if ((hostname === 'ragescream.com' || (hostname === 'localhost' && port === '3001')) && movie.pathseo) {
                          shareUrl = `https://sm.ragescream.com/ragescream/${movie.pathseo}`;
                        } else if ((hostname === 'standup-comedy.ch' || (hostname === 'localhost' && port === '3003')) && movie.pathseo) {
                          shareUrl = `https://sm.standup-comedy.ch/standup/${movie.pathseo}`;
                        } else if ((hostname === 'spkombat.com' || (hostname === 'localhost' && port === '3005')) && movie.pathseo) {
                          shareUrl = `https://sm.spkombat.com/spkombat/${movie.pathseo}`;
                        } else if ((hostname === 'jemmyx.com' || (hostname === 'localhost' && port === '3006')) && movie.pathseo) {
                          shareUrl = `https://sm.wildpeaks.tv/wildpeaks/${movie.pathseo}`;
                        } else if ((hostname === 'wildpeaks.tv' || (hostname === 'localhost' && port === '3009')) && movie.pathseo) {
                          shareUrl = `https://sm.wildpeaks.tv/wildpeaks/${movie.pathseo}`;
                        }
                        if (!shareUrl) return null;
                        return (
                          <>
                            <a
                              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-2 bg-blue-600 text-white rounded flex items-center gap-2 hover:bg-blue-700 transition"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.408.595 24 1.326 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.73 0 1.325-.592 1.325-1.326V1.326C24 .592 23.405 0 22.675 0"/></svg>
                              Partager sur Facebook
                            </a>
                            <a
                              href={`https://wa.me/?text=${encodeURIComponent(shareUrl)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-2 bg-green-500 text-white rounded flex items-center gap-2 hover:bg-green-600 transition"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="currentColor" className="w-5 h-5"><path d="M16.001 3.2c-7.05 0-12.8 5.75-12.8 12.8 0 2.262.601 4.482 1.741 6.422l-2.001 7.278 7.462-1.963c1.885 1.031 4.021 1.579 6.198 1.579h.001c7.05 0 12.799-5.75 12.799-12.8s-5.749-12.8-12.8-12.8zm6.922 19.722c-.297.828-1.729 1.613-2.383 1.713-.637.094-1.422.133-2.297-.148-.531-.168-1.211-.395-2.09-.773-3.684-1.594-6.086-5.309-6.273-5.563-.184-.254-1.5-2.004-1.5-3.828 0-1.824.969-2.723 1.313-3.094.344-.371.75-.465.988-.465.238 0 .494.004.711.013.228.01.533-.086.836.637.297.719 1.012 2.477 1.102 2.664.09.188.148.406.027.66-.121.254-.184.406-.363.625-.18.219-.383.488-.545.656-.18.184-.371.387-.16.762.211.371.938 1.547 2.012 2.508 1.379 1.23 2.539 1.617 2.91 1.801.371.184.586.156.801-.094.211-.25.902-1.051 1.145-1.414.238-.363.477-.301.801-.184.324.117 2.059.969 2.41 1.145.352.176.586.262.672.41.086.148.086.852-.211 1.68z"/></svg>
                              Partager sur WhatsApp
                            </a>
                            <a
                              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-2 bg-blue-700 text-white rounded flex items-center gap-2 hover:bg-blue-800 transition"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M20.447 20.452h-3.554v-5.569c0-1.327-.027-3.037-1.849-3.037-1.851 0-2.135 1.445-2.135 2.939v5.667H9.355V9h3.414v1.561h.049c.476-.9 1.637-1.849 3.369-1.849 3.601 0 4.268 2.368 4.268 5.452v6.288zM5.337 7.433c-1.144 0-2.07-.928-2.07-2.071 0-1.144.926-2.071 2.07-2.071 1.144 0 2.071.927 2.071 2.071 0 1.143-.927 2.071-2.071 2.071zm1.777 13.019H3.56V9h3.554v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.226.792 24 1.771 24h20.451c.979 0 1.778-.774 1.778-1.729V1.729C24 .774 23.203 0 22.225 0z"/></svg>
                              Partager sur LinkedIn
                            </a>
                            <button
                              onClick={() => navigator.clipboard.writeText(shareUrl)}
                              className="px-4 py-2 bg-gray-700 text-white rounded flex items-center gap-2 hover:bg-gray-800 transition"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M19 21H9c-1.1 0-2-.9-2-2V7c0-1.1.9-2 2-2h1V4c0-1.1.9-2 2-2h4c1.1 0 2 .9 2 2v1h1c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2zm-6-16h4v1h-4V5zm6 3H9v12h10V8zM5 19h10v2H5c-1.1 0-2-.9-2-2V9h2v10z"/></svg>
                              Copier le lien
                            </button>
                          </>
                        );
                      })()}
                    </div>

                    {/* Masqué: Duration section */}
                    {false && movie.runtime && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-400 mb-1">Duration</h4>
                        <p className="text-white">{movie.runtime} minutes</p>
                      </div>
                    )}

                    <div>
                      <h4 className="text-sm font-semibold text-gray-400 mb-1">Genres</h4>
                      <div className="flex flex-wrap gap-1 mb-4">
                        {movie.genre_ids?.map((genreId, index) => (
                          <span key={genreId} className="text-white text-sm">
                            {getGenreName(genreId)}
                            {index < movie.genre_ids.length - 1 && ', '}
                          </span>
                        ))}
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Fullscreen Video Player - Outside the modal backdrop to avoid conflicts */}
      <FullscreenVideoPlayer 
        movie={movie}
        isOpen={showFullscreen}
        onClose={handleCloseFullscreenAndModal}
        videos={videos}
        setCurrentVideo={handleSetCurrentVideo}
      />
    </>
  );
};

export default MovieModal;
